"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Wrench, ShieldCheck, Cpu, Layers } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const assessmentFactors = [{
  icon: Cpu,
  label: "Controller & Electronics",
  desc: "Console functionality, emergency stop relays, variable speed drives, and wiring integrity."
}, {
  icon: Wrench,
  label: "Mechanical Wear & Gearbox",
  desc: "Motor hours, dual-axis motion gearing, frame structure, and documented service history."
}, {
  icon: Layers,
  label: "Inflatable Mat & Blower",
  desc: "Commercial vinyl seam condition, baffling, blower output, and structural air retention."
}, {
  icon: ShieldCheck,
  label: "Replacement Parts Availability",
  desc: "Readily sourced spare components versus obsolete proprietary motion systems."
}];
export default function UsedPrices() {
  const prefersReducedMotion = useReducedMotion();
  const transitionConfig = prefersReducedMotion ? {
    duration: 0.1
  } : {
    duration: 0.3,
    ease: [0.16, 1, 0.3, 1] as const
  };
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        ...transitionConfig,
        staggerChildren: prefersReducedMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 8
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: transitionConfig
    }
  };
  return <section id="used-mechanical-bull-prices" data-nav="light" className="relative w-full border-y border-border bg-background py-16 text-foreground md:py-24">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="border border-border bg-card p-6 shadow-xs md:p-10 lg:p-12">
          {/* Top Spec Plate Header */}
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4 font-mono text-xs uppercase tracking-wider text-muted-foreground">
            <div className="flex items-center gap-2">
              <span className="inline-block size-2 bg-primary" />
              <span>VALUATION & ASSESSMENT CRITERIA</span>
            </div>
          </div>

          {/* Heading */}
          <motion.div variants={itemVariants} className="mb-6">
            <h2 className="font-serif text-3xl font-bold uppercase tracking-tight text-foreground sm:text-4xl md:text-5xl">
              Used Mechanical Bull Prices
            </h2>
          </motion.div>

          {/* Main Verbatim Copy Body */}
          <motion.div variants={itemVariants} className="space-y-4">
            <p className="max-w-4xl text-base leading-relaxed text-foreground md:text-lg">
              Used mechanical bull prices require more context because condition varies from one machine to another. Age alone does not determine value. Previous use, controller condition, inflatable condition, known repairs, included equipment and parts availability can all matter. A lower-priced used machine may still require additional spending if important components are missing or difficult to replace. Conversely, cosmetic wear does not automatically make a machine poor value if the relevant equipment information is clear. Compare the exact unit rather than relying on a generic used-equipment price range.
            </p>
          </motion.div>

          {/* Inspection Parameter Breakdown Grid */}
          <motion.div variants={itemVariants} className="my-8 grid grid-cols-1 gap-px border border-border bg-border sm:grid-cols-2">
            {assessmentFactors.map((factor, index) => {
            const IconComponent = factor.icon;
            return <div key={factor.label} data-index={index} className="bg-card p-5 transition-colors hover:bg-muted/40">
                  <div className="mb-2 flex items-center gap-2">
                    <IconComponent className="size-4 text-primary" />
                    <span className="font-mono text-xs font-semibold uppercase tracking-wider text-foreground">
                      {factor.label}
                    </span>
                  </div>
                  <p className="text-xs leading-normal text-muted-foreground">
                    {factor.desc}
                  </p>
                </div>;
          })}
          </motion.div>

          {/* Primary Action Button */}
          <motion.div variants={itemVariants} className="pt-2">
            <Button asChild className="h-12 rounded-none bg-primary px-8 font-serif text-base font-bold uppercase tracking-wider text-primary-foreground shadow-sm hover:bg-primary/90 focus-visible:ring-ring">
              <Link to="/UsedMechanicalBullsPage" className="inline-flex items-center gap-2">
                VIEW USED MECHANICAL BULLS
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}